import React, { createContext, useMemo } from 'react';
import { PropsWithChildren } from 'react';
import { observer } from 'mobx-react';
import { useGlobalCX } from '~/components/ClientExperience/providers/global-context/useGlobalCX';
import {
    HBStatsigProvider,
    type HBStatsigProviderInstance
} from '~/common/Statsig/HBStatsigProvider';
import { isReactFirst } from '~/utils/environment.utils';
import { useIsWrappedWithCXStatsigProvider } from '~/common/Statsig/Authenticated/CX/useIsWrappedWithCXStatsigProvider';
import { useWarnAboutNestedCXStatsigProvider } from '~/common/Statsig/Authenticated/CX/useWarnAboutNestedCXStatsigProvider';
import type { StatsigUserCustom } from '~/common/Statsig/types';

// Create a context to track nesting (if a StatsigProvider already exists as a parent)
export const HBCXStatsigProviderExistsContext = createContext<boolean>(false);

const HBCXPublicAccountStatsigProvider = ({
    children,
    name,
    // in react first every provider is nested, so we silence the warning
    silenceIsNestedWarning = isReactFirst() ? true : false
}: PropsWithChildren<HBStatsigProviderInstance>) => {
    const {
        configStore,
        networkStore,
        clientPortalConfigStore,
        clientPortalConfigStore: { configuration },
        userStore: { currUser }
    } = useGlobalCX();

    // this allows us to wait until configuration in set
    const memberAccountId = configuration
        ? clientPortalConfigStore?.memberAccountId
        : null;

    // PLEASE do not make any changes below
    const statsigUserCustomOverride = useMemo(
        (): StatsigUserCustom => ({
            custom: {
                account_id: memberAccountId,
                client_id: currUser?._id
                // created_at: currUser?.created_at,
                // user_source: currUser?.user_source
            },
            customIDs: {
                account_id: memberAccountId,
                client_id: currUser?._id
            }
        }),
        [memberAccountId, currUser?._id]
    );

    const isWrappedWithCXStatsigProvider = useIsWrappedWithCXStatsigProvider();

    useWarnAboutNestedCXStatsigProvider({
        silenceIsNestedWarning,
        name
    });

    // Use the context to determine if this provider is nested (already has a provider as parent)
    if (isWrappedWithCXStatsigProvider) {
        return <>{children}</>;
    }

    return (
        <HBStatsigProvider
            configStore={configStore}
            networkStore={networkStore}
            mountKey={memberAccountId}
            statsigUserCustomOverride={statsigUserCustomOverride}
        >
            {/*tell all children that they're wrapped with a provider*/}
            <HBCXStatsigProviderExistsContext.Provider value={true}>
                {children}
            </HBCXStatsigProviderExistsContext.Provider>
        </HBStatsigProvider>
    );
};

export default observer(HBCXPublicAccountStatsigProvider);
